import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import styles from './styles.module.scss';
import Button from '../../../../components/Button';
import ResultHeader from '../ResultHeader';
import { Fireworks } from './Fireworks';
import { Clouds } from './Clouds';

import { GAME_CONFIG, TGameType } from '../../helpers';
import { DifficultyLevel } from '../../../../shared/enums';

import { useAppSelector } from '../../../../core/redux/hooks/useAppSelector';
import { useAppDispatch } from '../../../../core/redux/hooks/useAppDispatch';
import { selectDifficulty, selectGameType } from '../../selectors';
import { setDifficulty } from '../../slice';
import MetroWithPeopleImage from '../../../../assets/images/metro/MetroWithPeople.svg';
import MetroEmptyImage from '../../../../assets/images/metro/MetroEmpty.svg';
import { isUserCanReceivePoints, isUserReceivedPoints } from '../../../../shared/utils';
import { useGameData } from '../../hooks/useGameData';
import { playAudioFromContext } from '../../../../shared/audioManager';

interface IResultContainerProps {
  isWon: boolean;
  audioSrc: string;
}

const getNextDifficulty = (current: string): DifficultyLevel | null => {
  if (!current) return null;

  const levels = Object.values(DifficultyLevel);
  const currentIndex = levels.indexOf(current as DifficultyLevel);
  return currentIndex !== -1 && currentIndex < levels.length - 1 ? levels[currentIndex + 1] : null;
};

const ResultContainer: React.FC<IResultContainerProps> = ({ isWon, audioSrc }) => {
  const { userData, scoreData } = useGameData();
  const dispatch = useAppDispatch();
  const gameType = useAppSelector(selectGameType) || '';
  const difficulty = useAppSelector(selectDifficulty) || '';

  useEffect(() => {
    playAudioFromContext(audioSrc);
  }, [audioSrc]);

  const navigate = useNavigate();

  const difficultyLevel = useMemo(() => (difficulty ? difficulty : '') as DifficultyLevel, [difficulty]);
  const nextDifficultyLevel = useMemo(() => getNextDifficulty(difficulty), [difficulty]);

  const handleBackClick = useCallback(() => {
    navigate(`/${gameType}/difficulty`);
  }, [gameType, navigate]);

  const handleReplayClick = useCallback(() => {
    if (difficulty) {
      navigate(`/${gameType}/game`);
    }
  }, [gameType, difficulty, navigate]);

  const handleNextClick = useCallback(() => {
    if (nextDifficultyLevel) {
      dispatch(setDifficulty(nextDifficultyLevel));
      navigate(`/${gameType}/game`);
    }
  }, [nextDifficultyLevel, dispatch, navigate, gameType]);

  const canShowPoints = isWon && !nextDifficultyLevel && isUserCanReceivePoints(userData) && !isUserReceivedPoints(scoreData);

  return (
    <div className={classNames(styles.resultContainer, { [styles.lost]: !isWon })}>
      <ResultHeader
        gameType={gameType}
        isWon={isWon}
        icon={GAME_CONFIG[gameType as TGameType].icons[difficultyLevel]}
        text={GAME_CONFIG[gameType as TGameType].text.singular[difficultyLevel]}
      />

      <div className={styles.contentWrapper}>
        <div className={styles.scalableWrapper}>
          <div className={styles.board}>
            <div className={styles.title}>{isWon ? 'Победа!' : 'Не угадал, попробуй ещё'}</div>
            {canShowPoints && <div className={styles.points}>+10 баллов</div>}
          </div>
        </div>

        <div className={styles.footer}>
          <img alt="" className={styles.metroImage} src={isWon ? MetroWithPeopleImage : MetroEmptyImage} />

          <div className={styles.buttons}>
            <Button variant={isWon ? 'blue' : 'secondary-blue'} icon="ui-icon-ArrowBack" onClick={handleBackClick} />
            <Button variant="red" icon="ui-icon-Replay" onClick={handleReplayClick} />

            {Boolean(nextDifficultyLevel) && isWon && (
              <Button variant={isWon ? 'blue' : 'secondary-blue'} icon="ui-icon-ArrowFront" onClick={handleNextClick} />
            )}
          </div>
        </div>
      </div>

      {isWon ? <Fireworks /> : <Clouds />}
    </div>
  );
};

export default ResultContainer;
